import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Activity, DoorOpen, Video, Phone, Loader, AlertCircle, ExternalLink,
  Shield, FileText,
} from 'lucide-react';

// ─────────────────────────────────────────────
// CONSTANTS / HELPERS (lightweight copy of the
// per-type meta from InvestigationDetail — kept
// independent so the public page has no coupling
// to the authenticated detail page.)
// ─────────────────────────────────────────────

const TYPE_META = {
  ACS_EVENT:    { icon: DoorOpen, color: '#5eead4', label: 'ACS Event' },
  VMS_CLIP:     { icon: Video,    color: '#60a5fa', label: 'VMS Clip' },
  FR_MATCH:     { icon: Activity, color: '#a855f7', label: 'FR Match' },
  INTERCOM_LOG: { icon: Phone,    color: '#fb923c', label: 'Intercom Log' },
};

const EVENT_TYPE_LABELS = {
  'access.granted':    'Access Granted',
  'access.denied':     'Access Denied',
  'access.forced':     'Door Forced',
  'access.held':       'Door Held',
  'intercom.call':     'Intercom Call',
  'intercom.answered': 'Intercom Answered',
  'intercom.missed':   'Intercom Missed',
  'fr.match':          'Face Match',
  'fr.alert':          'Watchlist Alert',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function eventTypeLabel(t) {
  if (!t) return '';
  if (EVENT_TYPE_LABELS[t]) return EVENT_TYPE_LABELS[t];
  return String(t).replace(/[._]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─────────────────────────────────────────────
// EVIDENCE ROW (read-only)
// ─────────────────────────────────────────────

function SharedEvidenceRow({ item }) {
  const M = TYPE_META[item.type] || TYPE_META.ACS_EVENT;
  const Icon = M.icon;
  const meta = item.metadata || {};
  const rowClass = `inv-evidence-row inv-evidence-row-${item.type.toLowerCase().split('_')[0]}`;

  let center = null;
  if (item.type === 'ACS_EVENT') {
    const evtLabel = meta.nativeType || eventTypeLabel(meta.eventType);
    center = (
      <>
        <span className="inv-evidence-actor">{meta.actorName || 'Unknown'}</span>
        {meta.actorType && <span className="inv-badge inv-badge-actor">{meta.actorType}</span>}
        {evtLabel && <span className="inv-evidence-evttype">{evtLabel}</span>}
      </>
    );
  } else if (item.type === 'VMS_CLIP') {
    center = (
      <>
        <span className="inv-evidence-evttype">Footage clip</span>
        {item.clipStart && item.clipEnd && (
          <span className="inv-evidence-sub">
            {new Date(item.clipStart).toLocaleTimeString()} → {new Date(item.clipEnd).toLocaleTimeString()}
          </span>
        )}
        {item.vmsClipUrl && (
          <a
            href={item.vmsClipUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inv-evidence-link"
          >
            View in VMS <ExternalLink size={11} />
          </a>
        )}
      </>
    );
  } else if (item.type === 'FR_MATCH') {
    const conf = typeof item.frConfidence === 'number'
      ? Math.round(item.frConfidence * (item.frConfidence > 1 ? 1 : 100))
      : null;
    center = (
      <>
        <span className="inv-evidence-actor">{item.frSubjectName || 'Unknown subject'}</span>
        {conf != null && <span className="inv-badge inv-badge-actor">{conf}% match</span>}
      </>
    );
  } else if (item.type === 'INTERCOM_LOG') {
    center = (
      <>
        <span className="inv-evidence-evttype">{eventTypeLabel(meta.eventType) || 'Intercom event'}</span>
        {meta.callStatus && <span className="inv-evidence-sub">Status: {meta.callStatus}</span>}
      </>
    );
  }

  return (
    <div className={rowClass} style={{ '--row-accent': M.color }}>
      <div className="inv-evidence-left">
        <Icon size={16} style={{ color: M.color, flexShrink: 0 }} />
        <div className="inv-evidence-left-info">
          <span className="inv-evidence-device">{item.deviceLabel || M.label}</span>
          {item.sourceSystem && (
            <span className="inv-badge inv-badge-system">{item.sourceSystem}</span>
          )}
        </div>
      </div>
      <div className="inv-evidence-center">{center}</div>
      <div className="inv-evidence-right">
        <span className="inv-evidence-time">{formatDateTime(item.eventTimestamp || item.createdAt)}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function SharedCaseView() {
  const { token } = useParams();
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Explicit Accept header so the backend serves JSON (the SPA HTML lives
    // at the same path and is served when the header is text/html).
    fetch(`/share/${token}`, {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })
      .then(async r => {
        if (cancelled) return null;
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || (r.status === 410
            ? 'This link has expired or is invalid'
            : r.status === 429
              ? 'Too many requests — please try again in a minute'
              : 'This link has expired or is invalid'));
        }
        return r.json();
      })
      .then(d => { if (!cancelled && d) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // ── States ──

  if (loading) {
    return (
      <div className="shared-page">
        <SharedHeader org={null} />
        <div className="shared-loading">
          <Loader size={22} className="spin" />
          <span>Loading shared case…</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="shared-page">
        <SharedHeader org={null} />
        <div className="shared-error">
          <AlertCircle size={48} />
          <h2>Link unavailable</h2>
          <p>{error || 'This link has expired or is invalid.'}</p>
        </div>
        <SharedFooter />
      </div>
    );
  }

  const { case: c, evidence = [], org, viewerName } = data;
  const incidentLine = c.incidentTimestamp
    ? `${formatDateTime(c.incidentTimestamp)} · ±${c.windowMinutes || 15} min window`
    : null;

  return (
    <div className="shared-page">
      <SharedHeader org={org} />

      <main className="shared-main">
        <section className="shared-case-card">
          <div className="shared-case-eyebrow">Investigation Report</div>
          <h1 className="shared-case-title">{c.title}</h1>
          <div className="shared-case-badges">
            <span className={`inv-badge inv-badge-${c.status}`}>
              {String(c.status).replace('_', ' ')}
            </span>
            <span className={`inv-badge inv-badge-${c.priority}`}>{c.priority}</span>
          </div>
          {incidentLine && (
            <div className="shared-case-incident">
              <strong>Incident:</strong> {incidentLine}
            </div>
          )}
          {viewerName && (
            <div className="shared-case-viewer">
              Prepared for: <strong>{viewerName}</strong>
            </div>
          )}
        </section>

        <section>
          <h2 className="shared-section-title">
            <FileText size={16} /> Evidence Timeline
            <span className="shared-section-count">
              {evidence.length} item{evidence.length === 1 ? '' : 's'}
            </span>
          </h2>

          {evidence.length === 0 ? (
            <div className="shared-empty">
              <AlertCircle size={32} />
              <span>No evidence items recorded for this case.</span>
            </div>
          ) : (
            <div className="inv-evidence-list">
              {evidence.map((item, idx) => (
                <SharedEvidenceRow key={idx} item={item} />
              ))}
            </div>
          )}
        </section>
      </main>

      <SharedFooter />
    </div>
  );
}

function SharedHeader({ org }) {
  const orgName = org?.name || 'ServerliCCM';
  return (
    <header className="shared-header">
      <div className="shared-header-left">
        {org?.logoUrl
          ? <img src={org.logoUrl} alt={orgName} className="shared-header-logo" />
          : <Shield size={20} className="shared-header-icon" />}
        <div className="shared-header-titles">
          <div className="shared-header-org">{orgName}</div>
          <div className="shared-header-sub">Investigation Report</div>
        </div>
      </div>
      <div className="shared-header-right">
        <span className="shared-powered">Powered by ServerliCCM</span>
      </div>
    </header>
  );
}

function SharedFooter() {
  return (
    <footer className="shared-footer">
      This report was shared from <strong>ServerliCCM</strong>. For access to the full platform,
      visit <a href="https://serverliccm.com" target="_blank" rel="noopener noreferrer">serverliccm.com</a>.
    </footer>
  );
}
